import * as React from 'react';
import { CookieConsentContainer } from '../../containers/CookieConsent';
import { NoSsr } from '@cian/react-utils';
import { CareerFooter } from './CareerFooter';
import { cx } from './styles';
import { useSelector } from 'react-redux';
import { selectDeviceType } from '../../selectors/page';
import { sendCareerNavigationEvent } from '../../utils/analytics/events/careerNavigation';

const backOut = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

const progress = (value: number): number => Math.max(0, Math.min(1, value));

export const CareerLayout: React.FC<React.PropsWithChildren> = ({ children }) => {
  const deviceType = useSelector(selectDeviceType);
  const rootRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const root = rootRef.current;
    if (!root || !('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
    const animations: Animation[] = [];
    const observers: IntersectionObserver[] = [];
    const getByClassName = (className: string): Element[] => Array.from(root.getElementsByClassName(cx(className)));

    const reveal = (elements: Element[], keyframes: Keyframe[], duration: number, stagger = 0): void => {
      elements.forEach((element, index) => {
        const htmlElement = element as HTMLElement;
        htmlElement.style.opacity = '0';
        const observer = new IntersectionObserver(
          entries => {
            if (!entries.some(entry => entry.isIntersecting)) {
              return;
            }
            const animation = element.animate(keyframes, {
              duration,
              delay: Math.min(index * stagger, 400),
              easing: backOut,
              fill: 'both',
            });
            animations.push(animation);
            animation.finished
              .then(() => {
                htmlElement.style.removeProperty('opacity');
                htmlElement.style.removeProperty('transform');
              })
              .catch(() => undefined);
            observer.unobserve(element);
          },
          { rootMargin: '0px 0px -10% 0px', threshold: 0 },
        );
        observer.observe(element);
        observers.push(observer);
      });
    };

    root.querySelectorAll('h2').forEach(heading => {
      const textNodes: Text[] = [];
      const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT);
      let node = walker.nextNode();
      while (node) {
        textNodes.push(node as Text);
        node = walker.nextNode();
      }

      const words: HTMLElement[] = [];
      textNodes.forEach(textNode => {
        const fragment = document.createDocumentFragment();
        textNode.data.split(/(\s+)/).forEach(part => {
          if (!part || /^\s+$/.test(part)) {
            fragment.append(part);

            return;
          }
          const word = document.createElement('span');
          word.className = cx('reveal-word');
          word.textContent = part;
          word.style.opacity = '0';
          word.style.transform = 'translateY(var(--career-unit))';
          words.push(word);
          fragment.append(word);
        });
        textNode.replaceWith(fragment);
      });

      const observer = new IntersectionObserver(
        entries => {
          if (!entries.some(entry => entry.isIntersecting)) {
            return;
          }
          words.forEach((word, index) => {
            const animation = word.animate(
              [
                { opacity: 0, transform: 'translateY(var(--career-unit))' },
                { opacity: 1, transform: 'translateY(0)' },
              ],
              {
                duration: 500,
                delay: words.length > 1 ? (index * 500) / (words.length - 1) : 0,
                easing: backOut,
                fill: 'both',
              },
            );
            animations.push(animation);
          });
          observer.unobserve(heading);
        },
        { rootMargin: '0px 0px -15% 0px', threshold: 0 },
      );
      observer.observe(heading);
      observers.push(observer);
    });

    reveal(
      getByClassName('main_about_card'),
      [
        { opacity: 0, transform: 'translateY(100%)' },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      1200,
      70,
    );
    reveal(
      getByClassName('icon-1x1-large').filter(element => element.classList.contains(cx('text-color-brand-orange'))),
      [
        { opacity: 1, transform: 'scale(0)' },
        { opacity: 1, transform: 'scale(1)' },
      ],
      700,
      55,
    );
    reveal(
      getByClassName('main_people_card-wrapper'),
      [
        { opacity: 0, transform: 'translateY(100%)' },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      1500,
      90,
    );
    reveal(
      getByClassName('main_offer_card'),
      [
        { opacity: 0, transform: 'translateY(70%)' },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      1000,
      60,
    );

    const mouse = getByClassName('main_team_mouse-image')[0] as HTMLElement | undefined;
    const verticalArrows = getByClassName('system-arrow-vertical') as HTMLElement[];
    const horizontalArrows = getByClassName('system-arrow-horizontal') as HTMLElement[];
    let frame = 0;

    const updateScrollAnimations = (): void => {
      frame = 0;
      const viewportHeight = window.innerHeight;
      if (mouse && mouse.offsetParent) {
        const rect = mouse.getBoundingClientRect();
        const endTop = (viewportHeight - rect.height) / 2;
        const amount = progress((viewportHeight - rect.top) / (viewportHeight - endTop));
        mouse.style.transform = `translateX(${15 * (1 - amount)}vw) scale(${0.7 + 0.3 * amount}) rotate(${
          10 * (1 - amount)
        }deg)`;
      }
      verticalArrows.forEach(arrow => {
        const rect = arrow.getBoundingClientRect();
        if (!rect.width && !rect.height) {
          return;
        }
        const startTop = viewportHeight / 2 - rect.height / 2;
        const amount = progress((startTop - rect.top) / (startTop + rect.height));
        arrow.style.transform = `translateX(${amount * 100}vw)`;
      });
      horizontalArrows.forEach(arrow => {
        const rect = arrow.getBoundingClientRect();
        if (!rect.width && !rect.height) {
          return;
        }
        const startTop = viewportHeight - rect.height / 2;
        const endTop = viewportHeight / 2 - rect.height;
        const amount = progress((startTop - rect.top) / (startTop - endTop));
        arrow.style.transform = `translateX(${-100 * (1 - amount)}vw)`;
      });
    };
    const requestScrollUpdate = (): void => {
      if (!frame) {
        frame = window.requestAnimationFrame(updateScrollAnimations);
      }
    };
    updateScrollAnimations();
    window.addEventListener('scroll', requestScrollUpdate, { passive: true });
    window.addEventListener('resize', requestScrollUpdate);

    return () => {
      observers.forEach(observer => observer.disconnect());
      animations.forEach(animation => animation.cancel());
      window.removeEventListener('scroll', requestScrollUpdate);
      window.removeEventListener('resize', requestScrollUpdate);
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, []);

  return (
    <div
      className={cx('root')}
      ref={rootRef}
      onClick={event => {
        const anchor = (event.target as Element).closest('a');
        if (anchor) {
          // Query parameters can contain personal data; only track the destination path.
          const destination = new URL(anchor.href, window.location.href);
          sendCareerNavigationEvent(destination.pathname + destination.hash, deviceType);
        }
      }}
    >
      <a href="#career-content" className={cx('skip-link')}>
        Перейти к содержимому
      </a>
      <main id="career-content">{children}</main>
      <CareerFooter />
      <NoSsr>
        <CookieConsentContainer appearance="career" />
      </NoSsr>
    </div>
  );
};
